import { useEffect, useRef, useState } from "react";
import { apiGetPublicPracticeBranding, type PublicPracticeBranding } from "@/lib/api";

type Branding = { logoUrl: string; practiceName: string };
let lastBranding: Branding | null = null;
let lastBrandingAt = 0;
let brandingInFlight: Promise<Branding | null> | null = null;

async function loadBranding(force = false): Promise<Branding | null> {
  if (!force && lastBranding && Date.now() - lastBrandingAt < 300_000) return lastBranding;
  if (brandingInFlight) return brandingInFlight;
  brandingInFlight = apiGetPublicPracticeBranding().then((response) => {
    const persisted = response.data?.branding as PublicPracticeBranding | undefined;
    if (!persisted) return lastBranding;
    lastBranding = {
      logoUrl: persisted.logo_url || "",
      practiceName: persisted.public_practice_name || "Perspective Therapy",
    };
    lastBrandingAt = Date.now();
    return lastBranding;
  }).catch(() => lastBranding).finally(() => { brandingInFlight = null; });
  return brandingInFlight;
}

export function usePublicPracticeBranding(enabled = true) {
  const [branding, setBranding] = useState<Branding>(lastBranding || { logoUrl: "", practiceName: "Perspective Therapy" });
  const [failedLogoUrl, setFailedLogoUrl] = useState("");
  const retryTimer = useRef<number | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    const loadPersistedBranding = async (force = false) => {
      const persisted = await loadBranding(force);
      if (active && persisted) setBranding(persisted);
    };
    const handler = () => { void loadPersistedBranding(true); };
    void loadPersistedBranding();
    window.addEventListener("practice-settings-updated", handler);
    return () => {
      active = false;
      if (retryTimer.current !== null) window.clearTimeout(retryTimer.current);
      window.removeEventListener("practice-settings-updated", handler);
    };
  }, [enabled]);
  return {
    ...branding,
    logoUrl: branding.logoUrl === failedLogoUrl ? "" : branding.logoUrl,
    clearLogo: () => {
      setFailedLogoUrl(branding.logoUrl);
      if (retryTimer.current !== null) window.clearTimeout(retryTimer.current);
      retryTimer.current = window.setTimeout(() => setFailedLogoUrl(""), 30_000);
    },
  };
}
